/**
 * Der Textbaustein, den der Betreiber in SEINE Datenschutzhinweise uebernimmt: welche
 * oeffentlichen Quellen MAESTRO zu einer Firma abfragt, wozu, und wie lange das Ergebnis bleibt.
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 6.6.4 („Transparenz gegenueber dem
 * Anfragenden: Die Datenschutzhinweise des Betreibers sollten die Anreicherung aus oeffentlichen
 * Registern benennen — OFFEN, Textbaustein zu formulieren"), Kriterium R4.6 in
 * docs/LOOP-RECHERCHE.md. Fristen: § 4.1 + `apps/api/src/lib/anreicherung-ttl.ts`.
 *
 * ── Wem der Text gehoert ─────────────────────────────────────────────────────────────────────
 * Dem Betreiber. MAESTRO ist Auftragsverarbeiter der Software, nicht Verfasser fremder
 * Datenschutzhinweise. Deshalb steht die Einschraenkung IM Text (`HAFTUNGSHINWEIS`) und nicht nur
 * in diesem Kommentar: wer den Baustein kopiert, liest sie mit. Das ist keine Floskel — der
 * Baustein beschreibt, was die Software TUT; ob das im Betrieb rechtmaessig ist (Rechtsgrundlage,
 * Interessenabwaegung, Verweise auf die uebrigen Abschnitte), entscheidet der Betreiber.
 *
 * ── Warum die Zahlen nicht abgeschrieben sind ────────────────────────────────────────────────
 * Ein Datenschutzhinweis, der „12 Monate" sagt, waehrend der Code 18 loescht, ist schlimmer als
 * keiner: er ist eine falsche Zusage an den Gast. Die Fristen stehen deshalb in
 * `TRANSPARENZ_FRISTEN` als EINE Stelle, und `apps/api/test/transparenz-hinweis.test.ts` haelt
 * sie gegen `apps/api/src/lib/anreicherung-ttl.ts` — die Stelle, an der der Code sie wirklich
 * anwendet. Aendert dort jemand eine Zahl, wird der Test rot, bis der Text nachzieht.
 * (Warum nicht direkt importiert? `packages/*` darf nicht auf `apps/api` zugreifen — die
 * Abhaengigkeit liefe verkehrt herum. Die Ratsche ersetzt den Import, sie ist sogar strenger:
 * sie verlangt fuer JEDE Frist-Konstante des Codes einen Eintrag hier.)
 *
 * ── Warum ein eigenes Paket ──────────────────────────────────────────────────────────────────
 * Wie `@maestro/recherche-stand` (R1.6) und `@maestro/google-attribution` (R4.5): `apps/web` hat
 * KEINEN Test-Runner, ein Text mit Rechtsfolgen darf nicht ungeprueft in einer Komponente liegen.
 * Dieses Paket hat bewusst KEIN `test`-Skript (Ratsche `apps/api/test/ci-testabdeckung.test.ts`).
 */

/* ────────────────────────────── Fristen ────────────────────────────────────────────────────── */

/** Die Aufbewahrungsfristen, die im Text genannt werden. */
export interface TransparenzFristen {
  handelsregisterMonate: number;
  bundesanzeigerMonate: number;
  placesMonate: number;
  placesRohdatenStunden: number;
  impressumMonate: number;
  ustIdMonate: number;
  /**
   * KEINE Haltbarkeit, sondern eine LOESCHfrist — der Unterschied ist fuer den Gast der
   * wichtigste im ganzen Baustein (R4.1, Konzept § 6.1.6): die Fristen oben sagen, ab wann eine
   * Angabe als nicht mehr aktuell gilt (sie bleibt dann sichtbar, gekennzeichnet); diese sagt,
   * wann sie endgueltig verschwindet, weil der Anlass ihrer Speicherung entfallen ist.
   */
  zweckbindungLoeschfristMonate: number;
}

/**
 * Die Zahlen, die im Baustein stehen. Von Antoine am 06.08.2026 bestaetigt (Handelsregister 12,
 * Bundesanzeiger 6, Places 3, Impressum 6, USt-IdNr. 6); die 24 Stunden fuer die Google-Rohantwort
 * und die 3 Monate der Zweckbindungs-Loeschfrist sind in `anreicherung-ttl.ts` hergeleitet
 * (R2.7 bzw. R4.1).
 */
export const TRANSPARENZ_FRISTEN: TransparenzFristen = {
  handelsregisterMonate: 12,
  bundesanzeigerMonate: 6,
  placesMonate: 3,
  placesRohdatenStunden: 24,
  impressumMonate: 6,
  ustIdMonate: 6,
  zweckbindungLoeschfristMonate: 3,
};

/**
 * Welche Konstante aus `apps/api/src/lib/anreicherung-ttl.ts` welche Frist hier traegt. Die
 * Ratsche liest diese Zuordnung — sie ist der Vertrag zwischen Code und Text.
 *
 * `USTID_TTL_MONATE` gab es bis R3.2 nicht: die Quelle war noch nicht gebaut (GATE-D offen). Genau
 * daran hat die Ratsche erkannt, dass der Abschnitt als „noch nicht aktiv" gekennzeichnet sein
 * musste. Seit die Konstante existiert (`apps/api/src/lib/anreicherung-ttl.ts`, 6 Monate), findet
 * die Verarbeitung wirklich statt — der Vermerk ist weg, die Zahl wird zitiert. Auch hier hat die
 * Ratsche den Wechsel erzwungen, nicht ein Merkzettel.
 *
 * `IMPRESSUM_TTL_MONATE` gab es bis R3.1 ebenfalls nicht; der Abschnitt trug deshalb denselben
 * Vermerk. Seit die Konstante existiert (`apps/api/src/lib/anreicherung-ttl.ts`, 6 Monate), findet
 * die Verarbeitung wirklich statt — der Vermerk ist weg, die Zahl wird zitiert. Die Ratsche hat
 * den Wechsel erzwungen, nicht ein Merkzettel: sie wurde in dem Moment rot, in dem die Konstante
 * angelegt wurde.
 */
export const FRISTEN_HERKUNFT: Readonly<Record<keyof TransparenzFristen, string>> = Object.freeze({
  handelsregisterMonate: "HANDELSREGISTER_TTL_MONATE",
  bundesanzeigerMonate: "BUNDESANZEIGER_TTL_MONATE",
  placesMonate: "PLACES_TTL_MONATE",
  placesRohdatenStunden: "PLACES_ROHDATEN_TTL_STUNDEN",
  impressumMonate: "IMPRESSUM_TTL_MONATE",
  ustIdMonate: "USTID_TTL_MONATE",
  zweckbindungLoeschfristMonate: "ZWECKBINDUNG_LOESCHFRIST_MONATE",
});

/* ────────────────────────────── Aufbau ─────────────────────────────────────────────────────── */

export interface TransparenzAbschnitt {
  titel: string;
  absaetze: readonly string[];
  /**
   * Die beschriebene Quelle wird von MAESTRO heute noch nicht abgefragt. Der Betreiber uebernimmt
   * den Abschnitt erst, wenn das Modul laeuft — ein Hinweis auf eine Erhebung, die nicht
   * stattfindet, ist genauso falsch wie eine fehlende.
   */
  nochNichtAktiv?: boolean;
}

export interface TransparenzBaustein {
  ueberschrift: string;
  abschnitte: readonly TransparenzAbschnitt[];
  haftungshinweis: string;
}

/** Steht am Ende jedes Bausteins — und am Ende jeder kopierten Fassung. */
export const HAFTUNGSHINWEIS =
  "Dieser Text ist ein Baustein für Ihre eigenen Datenschutzhinweise, keine Rechtsberatung. " +
  "Prüfen Sie ihn auf Ihren Betrieb — im Zweifel anwaltlich —, und passen Sie ihn an, sobald Sie " +
  "Recherche-Module ein- oder ausschalten. Verantwortlich für Ihre Datenschutzhinweise sind Sie " +
  "als Betreiber.";

/**
 * Der fertige Baustein.
 *
 * Die Reihenfolge folgt der Frage, die ein Gast stellt: Was passiert? Woher stammt es? Wozu?
 * Wie lange? Was passiert NICHT? Was kann ich tun?
 */
export function transparenzBaustein(fristen: TransparenzFristen = TRANSPARENZ_FRISTEN): TransparenzBaustein {
  return {
    ueberschrift: "Angaben aus öffentlichen Quellen zu Ihrem Unternehmen",
    abschnitte: [
      {
        titel: "Was wir ergänzen und warum",
        absaetze: [
          "Wenn Sie uns eine Anfrage senden und dabei ein Unternehmen angeben, ergänzen wir Ihre " +
            "Angaben um Informationen aus öffentlich zugänglichen Quellen. Wir tun das, um die " +
            "Anfrage richtig zuzuordnen, Anschrift und Firmierung für Angebot und Rechnung zu " +
            "prüfen und Rückfragen zu vermeiden.",
          "Diese Ergänzung betrifft das Unternehmen, nicht Ihre Person. Soweit dabei " +
            "personenbezogene Angaben anfallen (etwa der Name einer vertretungsberechtigten " +
            "Person aus dem Handelsregister), verarbeiten wir sie auf Grundlage unseres " +
            "berechtigten Interesses an einer zutreffenden Geschäftsanbahnung.",
        ],
      },
      {
        titel: "Welche Quellen wir abfragen",
        absaetze: [
          `Handelsregister (handelsregister.de): Firmierung, Rechtsform, Registernummer, ` +
            `Registergericht, Sitz und vertretungsberechtigte Personen. Wir bewahren das Ergebnis ` +
            `${fristen.handelsregisterMonate} Monate ab dem Abruf auf.`,
          `Bundesanzeiger: der Hinweis darauf, ob und wann Jahresabschlüsse veröffentlicht wurden. ` +
            `Wir lesen ausschließlich die Trefferliste, keine Einzeldokumente, und bewahren das ` +
            `Ergebnis ${fristen.bundesanzeigerMonate} Monate ab dem Abruf auf.`,
          `Google Maps: Name, Anschrift, Telefonnummer, Bewertung, Öffnungszeiten und ` +
            `Betriebsstatus des Unternehmenseintrags. Wir bewahren diese Angaben ` +
            `${fristen.placesMonate} Monate ab dem Abruf auf; die unveränderte Antwort von Google ` +
            `löschen wir bereits nach ${fristen.placesRohdatenStunden} Stunden. Dauerhaft bleibt ` +
            `nur die Kennung des Google-Eintrags, damit wir denselben Betrieb wiedererkennen. ` +
            `Bewertungstexte rufen wir nicht ab.`,
        ],
      },
      {
        // R4.1: Ablauf und Loeschung sind ZWEI verschiedene Dinge, und genau das ist die Angabe,
        // auf die es dem Gast ankommt — wann Daten ueber ihn verschwinden. Die Fristen im
        // Abschnitt darueber sagen nur, ab wann eine Angabe als nicht mehr aktuell gilt.
        titel: "Wann die Angaben veralten — und wann sie verschwinden",
        absaetze: [
          "Nach den oben genannten Fristen gilt eine Angabe bei uns als nicht mehr aktuell. Sie " +
            "verschwindet dadurch nicht: Sie bleibt sichtbar, ist aber ausdrücklich als veraltet " +
            "gekennzeichnet, damit niemand unbemerkt mit einem alten Stand arbeitet. Wir fragen " +
            "die Quelle danach erneut ab. Bleibt eine solche Nachfrage ohne Ergebnis, etwa weil " +
            "das Register vorübergehend nicht erreichbar ist, behalten wir den bisherigen Stand " +
            "samt seinem Datum bei, statt ihn durch eine Lücke zu ersetzen.",
          `Etwas anderes gilt, wenn der Anlass der Speicherung entfällt — wenn also weder eine ` +
            `Anfrage noch ein Kundendatensatz bei uns dieses Unternehmen mehr führt. Dann ist der ` +
            `Zweck der Speicherung erledigt, und die Angaben aus Handelsregister und ` +
            `Bundesanzeiger löschen wir spätestens ${fristen.zweckbindungLoeschfristMonate} ` +
            `Monate nach Ablauf der oben genannten Frist endgültig. Wird ein Kundendatensatz auf ` +
            `Wunsch oder nach unseren allgemeinen Fristen gelöscht oder anonymisiert, entfernen ` +
            `wir diese Angaben sofort mit — nicht erst nach Fristablauf.`,
        ],
      },
      {
        titel: "Impressum der Unternehmenswebsite",
        absaetze: [
          `Ist zu Ihrem Unternehmen eine Website hinterlegt, rufen wir diese Website auf und lesen ` +
            `die Angaben aus, die dort nach § 5 des Digitale-Dienste-Gesetzes ohnehin ` +
            `veröffentlicht werden müssen: Firmierung und Rechtsform, Registernummer und ` +
            `Registergericht, Umsatzsteuer-Identifikationsnummer, die vertretungsberechtigten ` +
            `Personen und die Anschrift. Wir bewahren dieses Ergebnis ` +
            `${fristen.impressumMonate} Monate ab dem Abruf auf.`,
          "Dabei entstehen personenbezogene Daten: ein Impressum nennt die vertretungsberechtigten " +
            "Personen namentlich — etwa die Geschäftsführerin, den Geschäftsführer oder die " +
            "Inhaberin bzw. den Inhaber. Diese Namen speichern wir gemeinsam mit den übrigen " +
            "Angaben zum Unternehmen und löschen sie gemeinsam mit ihnen.",
          "Wir holen dabei nur die Impressum-Seite, höchstens einmal je Unternehmen. Die Website " +
            "durchsuchen wir nicht, weitere Inhalte lesen und speichern wir nicht, und die " +
            "Vorgaben der robots.txt der jeweiligen Website beachten wir: untersagt sie den " +
            "automatisierten Abruf, findet er nicht statt.",
        ],
      },
      {
        titel: "Bestätigung der Umsatzsteuer-Identifikationsnummer",
        absaetze: [
          `Ist zu Ihrem Unternehmen eine Umsatzsteuer-Identifikationsnummer aus einem anderen ` +
            `EU-Mitgliedstaat hinterlegt, lassen wir sie beim Bundeszentralamt für Steuern ` +
            `bestätigen (Bestätigungsabfrage nach § 18e des Umsatzsteuergesetzes). Übermittelt ` +
            `werden dabei Ihre Nummer und – für die qualifizierte Abfrage – Firmierung, Ort und ` +
            `gegebenenfalls Straße und Postleitzahl; das Amt teilt uns mit, ob diese Angaben mit ` +
            `den dort geführten übereinstimmen. Für deutsche Nummern gibt es kein solches ` +
            `Verfahren; sie fragen wir nicht ab.`,
          `Wir speichern das Ergebnis, den Wortlaut der Auskunft und den Zeitpunkt, auf den sich ` +
            `die Auskunft bezieht – dieser Zeitpunkt ist der eigentliche steuerliche Nachweis. Es ` +
            `ist ausdrücklich eine Momentaufnahme und keine dauerhafte Aussage über Ihre Nummer: ` +
            `Nach ${fristen.ustIdMonate} Monaten weisen wir den Nachweis als nicht mehr aktuell ` +
            `aus und holen bei Bedarf eine neue Bestätigung ein.`,
        ],
      },
      {
        titel: "Was wir nicht tun",
        absaetze: [
          "Wir erstellen aus diesen Angaben keine Bonitäts-, Zahlungs- oder Verhaltensprognose " +
            "über eine Person. Es findet keine automatisierte Entscheidung über Ihre Anfrage statt " +
            "— ob und zu welchen Bedingungen wir ein Angebot machen, entscheidet ein Mensch.",
          "Wir kaufen keine Daten zu und geben die abgerufenen Angaben nicht an Dritte weiter.",
        ],
      },
      {
        titel: "Was mit übernommenen Angaben geschieht",
        absaetze: [
          "Angaben, die wir nach Prüfung in unsere Kundendaten übernehmen — etwa eine korrigierte " +
            "Anschrift oder Firmierung —, sind ab diesem Zeitpunkt Teil unserer " +
            "Geschäftsunterlagen. Für sie gelten die allgemeinen Aufbewahrungs- und " +
            "Löschfristen dieser Datenschutzhinweise, nicht mehr die oben genannten.",
        ],
      },
      {
        titel: "Ihre Rechte",
        absaetze: [
          "Sie können jederzeit Auskunft darüber verlangen, welche Angaben wir zu Ihnen und Ihrem " +
            "Unternehmen gespeichert haben, deren Berichtigung oder Löschung verlangen und der " +
            "Verarbeitung widersprechen. Die Kontaktdaten dafür finden Sie am Anfang dieser " +
            "Datenschutzhinweise.",
        ],
      },
    ],
    haftungshinweis: HAFTUNGSHINWEIS,
  };
}

/** Der Vermerk an noch nicht aktiven Abschnitten — im Text wie in der Oberflaeche derselbe. */
export const NOCH_NICHT_AKTIV_VERMERK =
  "[noch nicht aktiv — erst übernehmen, wenn das Modul in MAESTRO eingeschaltet ist]";

/**
 * Formatiert einen Baustein als reinen Text — genau das, was ein Kopieren-Knopf in die
 * Zwischenablage legt. Von beiden Bausteinen gemeinsam genutzt, damit es nur EINE Stelle gibt,
 * die den „noch nicht aktiv"-Vermerk korrekt mitkopiert.
 *
 * Abschnitte, die noch nicht aktiv sind, tragen den Vermerk sichtbar mit: wer den Text kopiert,
 * soll nicht versehentlich eine Erhebung ankuendigen, die gar nicht stattfindet.
 */
export function bausteinAlsText(baustein: TransparenzBaustein): string {
  const teile: string[] = [baustein.ueberschrift, ""];
  for (const abschnitt of baustein.abschnitte) {
    teile.push(abschnitt.nochNichtAktiv ? `${abschnitt.titel} ${NOCH_NICHT_AKTIV_VERMERK}` : abschnitt.titel);
    for (const absatz of abschnitt.absaetze) teile.push(absatz);
    teile.push("");
  }
  teile.push(baustein.haftungshinweis);
  return teile.join("\n");
}

export function transparenzText(fristen: TransparenzFristen = TRANSPARENZ_FRISTEN): string {
  return bausteinAlsText(transparenzBaustein(fristen));
}

/* ────────────────────────── Versand-Baustein (Brevo) ───────────────────────────────────────── */

/**
 * ZWEITER, eigenstaendiger Textbaustein: der E-Mail-Versand ueber Brevo.
 *
 * Anlass (Antoine, 03.09.2026): Nach dem Umstieg auf Brevo (02.09.2026, `lib/brevo-mailer.ts`)
 * fragte er, ob im Frontend etwas zur DSGVO-Konformitaet stehen muesste, und welche der ueblichen
 * Anforderungen MAESTRO schon erfuellt. Der Befund: Der bestehende Baustein oben deckt NUR die
 * Firmen-Anreicherung aus oeffentlichen Registern ab — der Versand ueber einen externen
 * Dienstleister kam darin gar nicht vor, obwohl er personenbezogene Daten (Empfaengeradresse,
 * Nachrichteninhalt) an einen Dritten gibt. Genau diese Luecke schliesst dieser Baustein.
 *
 * ── Warum ein EIGENER Baustein und kein weiterer Abschnitt oben ────────────────────────────────
 * Die Ueberschrift dort lautet „Angaben aus oeffentlichen Quellen zu Ihrem Unternehmen". Ein
 * Abschnitt ueber Mailversand unter dieser Ueberschrift waere selbst eine kleine Irrefuehrung —
 * und wer nur den einen Baustein kopiert, hat den anderen dann versehentlich mit uebernommen oder
 * verloren. Zwei Themen, zwei Bausteine, zwei Kopieren-Knoepfe.
 *
 * ── Was hier BEWUSST NICHT steht ───────────────────────────────────────────────────────────────
 * Keine Aussage ueber einen Auftragsverarbeitungsvertrag (AVV) mit Brevo. Ob ein solcher Vertrag
 * besteht, ist eine Tatsache ueber das Konto des Betreibers, die dieses Paket nicht kennt und
 * deshalb nicht behaupten darf. Der AVV ist Pflicht (Art. 28 DSGVO), aber er ist vom Betreiber
 * abzuschliessen, nicht von der Software — der Haftungshinweis am Ende sagt genau das.
 *
 * Ebenfalls bewusst nicht: Double-Opt-In und Anmeldeformulare. Das sind Newsletter-Themen. MAESTRO
 * versendet ueber Brevo ausschliesslich einzelne, erwartete Nachrichten zu einer konkreten Anfrage
 * (Angebot, Bestaetigung, Rechnung) — Vertragserfuellung, keine Werbung mit Einwilligung.
 */
export function versandTransparenzBaustein(): TransparenzBaustein {
  return {
    ueberschrift: "Versand von E-Mails über einen externen Dienstleister",
    abschnitte: [
      {
        titel: "Was wir versenden und wie",
        absaetze: [
          "Wenn Sie zu Ihrer Anfrage ein Angebot, eine Auftragsbestätigung, eine Rechnung oder " +
            "eine andere Nachricht von uns erhalten, verschicken wir diese E-Mail nicht selbst, " +
            "sondern über den Dienstleister Brevo (Sendinblue SAS, Paris, Frankreich). Brevo " +
            "erhält dabei die E-Mail-Adresse der Empfängerin bzw. des Empfängers und den Inhalt " +
            "der jeweiligen Nachricht — ausschließlich zu dem Zweck, sie zuzustellen.",
        ],
      },
      {
        titel: "Auf welcher Grundlage",
        absaetze: [
          "Der Versand dieser Nachrichten dient der Abwicklung Ihrer Anfrage beziehungsweise " +
            "Ihres Auftrags. Er erfolgt auf Grundlage der Vertragserfüllung und der " +
            "vorvertraglichen Anbahnung nach Artikel 6 Absatz 1 Buchstabe b DSGVO — nicht auf " +
            "Grundlage einer Einwilligung, wie sie für einen Newsletter nötig wäre.",
        ],
      },
      {
        titel: "Wo die Daten verarbeitet werden",
        absaetze: [
          "Brevo (Sendinblue SAS) hat seinen Sitz in Paris, Frankreich, und unterliegt damit " +
            "unmittelbar der Datenschutz-Grundverordnung. Nach Angaben des Anbieters werden die " +
            "Daten innerhalb der Europäischen Union verarbeitet und gespeichert.",
        ],
      },
      {
        titel: "Was wir dabei nicht tun",
        absaetze: [
          "Wir nutzen diesen Versandweg ausschließlich für einzelne, von Ihnen erwartete " +
            "Nachrichten zu Ihrer Anfrage oder Ihrem Auftrag — nicht für Newsletter, nicht für " +
            "Werbung und nicht, um Ihr Verhalten auszuwerten.",
        ],
      },
    ],
    haftungshinweis: HAFTUNGSHINWEIS,
  };
}

export function versandTransparenzText(): string {
  return bausteinAlsText(versandTransparenzBaustein());
}

/* ────────────────────── Angebotsabruf-Baustein (Einblicke, Migration 730) ──────────────────── */

/**
 * DRITTER, eigenstaendiger Textbaustein: was beim Abruf eines Angebots ueber den persoenlichen
 * Link protokolliert wird (Antoine, 19.10.2026: „zaehle so viel wie moeglich … die legal sind").
 *
 * Der Satz im ersten Absatz ist der von Antoine vorgegebene Wortlaut. Er beschreibt genau das,
 * was `angebots_aufrufe` (sql/730) speichert: keine IP-Adresse und keine Browserkennung im
 * Abrufdatensatz (nur eine grobe Geraeteklasse), keine Cookies, kein Standort, kein Tracking-Pixel
 * in E-Mails. Die Ratenbegrenzung haelt `slug:IP` bis ca. 24 Stunden in `rate_limit_hits` - das
 * sagt der Text offen.
 */
export function angebotsAbrufBaustein(): TransparenzBaustein {
  return {
    ueberschrift: "Abruf von Angeboten über den persönlichen Link",
    abschnitte: [
      {
        titel: "Was wir protokollieren und warum",
        absaetze: [
          "Beim Abruf eines Angebots über den persönlichen Link protokollieren wir Zeitpunkt, " +
            "Geräteklasse, Verweildauer und Lesefortschritt, um den Vorgang zu bearbeiten " +
            "(Art. 6 Abs. 1 lit. b DSGVO). IP-Adresse und Browserkennung werden nicht zusammen mit dem " +
            "Abruf gespeichert; zur Missbrauchsabwehr wird die IP-Adresse kurzzeitig (höchstens " +
            "24 Stunden) getrennt vorgehalten.",
          "Die Geräteklasse unterscheidet nur zwischen Handy, Tablet und Computer. Dafür werden " +
            "weder Cookies noch Speicher im Browser verwendet, und es kommen keine Dienste " +
            "Dritter zum Einsatz.",
        ],
      },
      {
        titel: "Wie lange",
        absaetze: [
          "Diese Angaben gehören zum jeweiligen Vorgang und werden mit ihm gelöscht.",
        ],
      },
    ],
    haftungshinweis: HAFTUNGSHINWEIS,
  };
}

export function angebotsAbrufText(): string {
  return bausteinAlsText(angebotsAbrufBaustein());
}
